import get from "lodash.get";

import Abilities from "services/permissions/abilities";
import ContractUtils from "services/contract/utils";
import MapActioner from "services/map/actioner";
import MapUtils from "services/map/utils";
import MapApi from "services/map/api";
import IntlUtils from "services/intl/utils";
import AreaUtils from "services/areas/utils";
import ParkStatisticsApi from "./api";
import ParkStatisticsUtils from "./utils";
import ParkStatisticsActionTypes from "./types";
import { toast } from "react-toastify";
import GlobalFiltersActioner from "services/globalFilters/actioner";
import ParkedCarsActioner from "services/parkedCars/actioner";
import AvailableParkingActioner from "services/availableParking/actioner";
import HeatmapOccupancyActioner from "services/heatmapOccupancy/actioner";

class ParkStatisticsActioner {
  static setGeneralStats(stats) {
    return {
      type: ParkStatisticsActionTypes.SET_GENERAL_STATS,
      stats,
    };
  }

  static setDetailsStats(stats) {
    return {
      type: ParkStatisticsActionTypes.SET_DETAILS_STATS,
      stats,
    };
  }

  static setFilters(data) {
    return {
      type: ParkStatisticsActionTypes.SET_FILTERS,
      data,
    };
  }

  static setFilter(key, value) {
    return {
      type: ParkStatisticsActionTypes.SET_FILTER,
      key,
      value,
    };
  }

  static setControlsStats(stats) {
    return {
      type: ParkStatisticsActionTypes.SET_CONTROLS_STATS,
      stats,
    };
  }

  static setDailyAttendanceStats(stats) {
    return {
      type: ParkStatisticsActionTypes.SET_DAILY_ATTENDANCE_STATS,
      stats,
    };
  }

  static errorOnRetrieve(error) {
    return {
      type: ParkStatisticsActionTypes.ERROR_ON_RETRIEVE,
      error,
    };
  }

  static removeError() {
    return {
      type: ParkStatisticsActionTypes.REMOVE_ERROR,
    };
  }

  static setLoading(statName) {
    return {
      type: ParkStatisticsActionTypes.SET_LOADING,
      statName,
    };
  }

  static setAutoRefresh(autoRefresh) {
    return {
      type: ParkStatisticsActionTypes.SET_AUTO_REFRESH,
      autoRefresh,
    };
  }

  static setInitialized(value) {
    return {
      type: ParkStatisticsActionTypes.SET_INITIALIZED,
      value,
    };
  }

  static setMap(map) {
    return {
      type: ParkStatisticsActionTypes.SET_MAP_INFOS,
      map,
    };
  }

  static setSelectedLayers(layers) {
    return {
      type: ParkStatisticsActionTypes.SET_SELECTED_LAYERS,
      layers,
    };
  }

  static setAttendanceTimeLimit(limitTime) {
    return {
      type: ParkStatisticsActionTypes.SET_ATTENDANCE_TIME_LIMIT,
      limitTime,
    };
  }

  static retrieveLayersInfos() {
    return (dispatch, getState) => {
      const { autoRefresh } = getState().ParkStatistics;
      const { contract } = getState().Contract;

      if (!autoRefresh) {
        dispatch(ParkStatisticsActioner.setLoading("details"));
      }

      return MapApi.getAreasInfos({
        contract_id: contract?.id,
        types: ["PARK", "PARKANDFLOW", "UNEQUIPPED"],
      })
        .then((layersInfos) => {
          const { config } = getState()?.Map;
          const mapParkingConfig = config?.parking || {};
          const mapAislesConfig = config?.aisles || {};

          AreaUtils.sortAreasByConfigMapAreasOrder(
            layersInfos.areas,
            mapParkingConfig.areas
          );

          const layers = {
            ...layersInfos,
            aisles: mapAislesConfig?.configs || null,
          };

          const formattedMapInfos =
            ParkStatisticsUtils.formatLayersInfosFromApi(
              layers,
              mapParkingConfig
            );
          const currentSelectedLayers = get(
            getState(),
            "ParkStatistics.map.selectedLayers"
          );

          dispatch(ParkStatisticsActioner.setMap(formattedMapInfos));
          dispatch(
            MapActioner.setConfigParam(
              "aislesDisplayed",
              mapAislesConfig?.is_displayed_by_default
            )
          );
          dispatch(
            ParkStatisticsActioner.setSelectedLayers(
              currentSelectedLayers ||
                ParkStatisticsUtils.getDefaultSelectedLayers(formattedMapInfos)
            )
          );
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    };
  }

  static retrieveAllStats = (allAreas = false) => {
    return (dispatch, getState) => {
      const { map, filters } = getState().ParkStatistics;
      const { contract } = getState().Contract;

      if (!map?.initialized) {
        return Promise.resolve();
      }

      dispatch(ParkStatisticsActioner.setLoading("general"));
      dispatch(ParkStatisticsActioner.setLoading("controls"));
      dispatch(ParkStatisticsActioner.setLoading("dailyAttendance"));
      dispatch(ParkStatisticsActioner.setLoading("details"));

      const apiFilters = ParkStatisticsUtils.getFilters(
        "areas",
        getState(),
        allAreas
      );

      // Don't refresh if any areas selected
      if (
        ParkedCarsActioner.missingRequiredFilters("areas", apiFilters) ||
        AvailableParkingActioner.missingRequiredFilters("areas", apiFilters)
      ) {
        const parkedCarsFormattedList =
          ParkedCarsActioner.utils.formatListFromApi([]);
        dispatch(ParkedCarsActioner.setList(parkedCarsFormattedList));
        const availableParkingFormattedList =
          AvailableParkingActioner.utils.formatListFromApi([]);
        dispatch(
          AvailableParkingActioner.setList(availableParkingFormattedList)
        );
        return Promise.resolve();
      }

      return ParkStatisticsApi.getAllStats(apiFilters)
        .then((response) => {
          dispatch(ParkStatisticsActioner.setGeneralStats(response.general));
          dispatch(ParkStatisticsActioner.setControlsStats(response.controls));
          dispatch(
            ParkStatisticsActioner.setDailyAttendanceStats(
              response.daily_attendance
            )
          );
          const formattedOccupancyStats =
            ParkStatisticsUtils.formatDetailsStatsFromApi(response.occupancy);
          dispatch(
            ParkStatisticsActioner.setDetailsStats(formattedOccupancyStats)
          );

          dispatch(ParkStatisticsActioner.setInitialized(true));

          if (
            map?.initialized &&
            MapUtils.toggleMarkersAndAreas(
              filters?.timestamp,
              ContractUtils.getParkMaxDaysAllowed(contract)
            ) &&
            Abilities.ability.can("read", "park_stats.displayCircles")
          ) {
            dispatch(
              GlobalFiltersActioner.setParameter(
                "timestamp",
                response.timestamp,
                false
              )
            );

            // prevents the map from refreshing if the user has selected some areas
            if (!allAreas) {
              dispatch(
                ParkedCarsActioner.setList({
                  items: response.parked_cars,
                  others: { timestamp: response.timestamp },
                })
              );
              dispatch(
                AvailableParkingActioner.setList({
                  items: response.available_parking,
                  others: { timestamp: response.timestamp },
                })
              );
              dispatch(
                ParkStatisticsActioner.setFilter(
                  "timestamp",
                  response.timestamp
                )
              );
            }
          }

          if (
            map?.initialized &&
            MapUtils.toggleHeatmap(
              filters?.timestamp,
              ContractUtils.getParkMaxDaysAllowed(contract)
            )
          ) {
            dispatch(HeatmapOccupancyActioner.retrieveList("radius")).then(
              (result) => {
                const apiTimestamp = result?.value?.others?.timestamp;

                if (apiTimestamp) {
                  dispatch(
                    ParkStatisticsActioner.setFilter("timestamp", apiTimestamp)
                  );
                }
              }
            );
          }

          return response;
        })
        .catch((error) => {
          dispatch(ParkStatisticsActioner.errorOnRetrieve(error));
          return Promise.reject(error);
        })
        .finally(() => {
          dispatch(ParkStatisticsActioner.setLoading(null));
        });
    };
  };

  // Check if some required filters are missing
  static missingRequiredFilters(type = "default", filters) {
    if (
      type === "areas" &&
      (!get(filters, "areas") ||
        !get(filters, "timestamp") ||
        get(filters, "timestamp") < 0)
    ) {
      return true;
    }

    if (
      type === "radius" &&
      (!get(filters, "radius") ||
        get(filters, "radius") <= 0 ||
        !get(filters, "position") ||
        !get(filters, "position.lat") ||
        !get(filters, "position.lng"))
    ) {
      return true;
    }

    return false;
  }

  static retrieveGeneralStats(type = "areas", allAreas = false) {
    return (dispatch, getState) => {
      let filters = ParkStatisticsUtils.getFilters(type, getState(), allAreas);
      const ParkStatisticsState = getState().ParkStatistics;

      // Don't refresh if any areas selected
      if (ParkStatisticsActioner.missingRequiredFilters(type, filters)) {
        return Promise.resolve([]);
      }

      if (!get(ParkStatisticsState, "autoRefresh", false)) {
        dispatch(ParkStatisticsActioner.setLoading("general"));
      }

      if (type === "radius") {
        const contractId = getState().Contract.contract.id;
        filters = {
          ...filters,
          contract_id: contractId,
        };
      }

      return ParkStatisticsApi.getGeneralStats(type, filters)
        .then((stats) => {
          dispatch(ParkStatisticsActioner.setGeneralStats(stats));
        })
        .catch((error) => {
          dispatch(ParkStatisticsActioner.setGeneralStats({ error }));
        });
    };
  }

  static retrieveDetailsStats(type = "areas", allAreas = false) {
    return (dispatch, getState) => {
      let filters = ParkStatisticsUtils.getFilters(
        `${type}Details`,
        getState(),
        allAreas
      );

      // Don't refresh if any areas selected
      if (ParkStatisticsActioner.missingRequiredFilters(type, filters)) {
        return Promise.resolve([]);
      }

      return ParkStatisticsApi.getDetailsStats(type, filters).then((stats) => {
        const formattedStats =
          ParkStatisticsUtils.formatDetailsStatsFromApi(stats);
        dispatch(ParkStatisticsActioner.setDetailsStats(formattedStats));
      });
    };
  }

  static retrieveControlsStats(type = "areas", allAreas = false) {
    return (dispatch, getState) => {
      let filters = ParkStatisticsUtils.getFilters(type, getState(), allAreas);
      const ParkStatisticsState = getState().ParkStatistics;

      // Don't refresh if any areas selected
      if (ParkStatisticsActioner.missingRequiredFilters(type, filters)) {
        return Promise.resolve([]);
      }

      if (!get(ParkStatisticsState, "autoRefresh", false)) {
        dispatch(ParkStatisticsActioner.setLoading("controls"));
      }

      if (type === "radius") {
        const contractId = getState().Contract.contract.id;
        filters = {
          ...filters,
          contract_id: contractId,
        };
      }

      return ParkStatisticsApi.getControlsStats(type, filters)
        .then((stats) => {
          dispatch(ParkStatisticsActioner.setControlsStats(stats));
        })
        .catch((error) => {
          dispatch(ParkStatisticsActioner.setControlsStats({ error }));
        });
    };
  }

  static retrieveDailyAttendanceStats(type = "areas", allAreas = false) {
    return (dispatch, getState) => {
      let filters = ParkStatisticsUtils.getFilters(type, getState(), allAreas);
      const ParkStatisticsState = getState().ParkStatistics;

      // Don't refresh if any areas selected
      if (ParkStatisticsActioner.missingRequiredFilters(type, filters)) {
        return Promise.resolve([]);
      }

      if (!get(ParkStatisticsState, "autoRefresh", false)) {
        dispatch(ParkStatisticsActioner.setLoading("dailyAttendance"));
      }

      if (type === "radius") {
        const contractId = getState().Contract.contract.id;
        filters = {
          ...filters,
          contract_id: contractId,
        };
      }

      return ParkStatisticsApi.getDailyAttendanceStats(type, filters)
        .then((stats) => {
          dispatch(ParkStatisticsActioner.setDailyAttendanceStats(stats));
        })
        .catch((error) => {
          dispatch(ParkStatisticsActioner.setDailyAttendanceStats({ error }));
        });
    };
  }

  static lockSpace(uid, isLocked) {
    return (dispatch, getState) => {
      const state = getState();
      const currentContractID = state.Contract.contract.id;
      ParkStatisticsApi.lockSpace(uid, currentContractID, isLocked).then(() => {
        const intl = IntlUtils.getIntlInstance();
        let msg = intl.formatMessage({ id: "toast.success.spaceLocked" });
        if (!isLocked) {
          msg = intl.formatMessage({ id: "toast.success.spaceUnlocked" });
        }
        toast(msg, {
          position: toast.POSITION.BOTTOM_RIGHT,
          type: toast.TYPE.SUCCESS,
        });
      });
    };
  }
}

export default ParkStatisticsActioner;
